import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../services/adminApi';

interface Stats {
    singlePlayerGames: number;
    multiplayerGames: number;
    totalPlayers: number;
    onlinePlayers: number;
    activePaidSubscriptions: number;
    activeFreeTrials: number;
    playerTrialInvites: number;
}

interface NewsfeedItem {
    id: string;
    title: string;
    description?: string;
    mediaUrl?: string;
    mediaType?: string;
    isActive: boolean;
    created: string;
    updated?: string;
}

interface FeedbackItem {
    id: string;
    playerId: string;
    text: string;
    created: string;
}

const AdminDashboard: React.FC = () => {
    // Initialize activeTab from query string or default to 'stats'
    const getInitialTab = (): 'stats' | 'newsfeed' | 'feedback' => {
        const urlParams = new URLSearchParams(window.location.search);
        const tabParam = urlParams.get('tab');
        if (tabParam && ['stats', 'newsfeed', 'feedback'].includes(tabParam)) {
            return tabParam as 'stats' | 'newsfeed' | 'feedback';
        }
        return 'stats';
    };

    const [activeTab, setActiveTab] = useState<'stats' | 'newsfeed' | 'feedback'>(getInitialTab);
    const [stats, setStats] = useState<Stats | null>(null);
    const [newsfeedItems, setNewsfeedItems] = useState<NewsfeedItem[]>([]);
    const [feedbackItems, setFeedbackItems] = useState<FeedbackItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Function to update the query string when tab changes
    const updateQueryString = (tab: 'stats' | 'newsfeed' | 'feedback') => {
        const url = new URL(window.location.href);
        url.searchParams.set('tab', tab);
        window.history.replaceState({}, '', url.toString());
    };

    // Function to handle tab changes
    const handleTabChange = (tab: 'stats' | 'newsfeed' | 'feedback') => {
        setActiveTab(tab);
        updateQueryString(tab);
    };

    // Newsfeed form state
    const [showNewsfeedForm, setShowNewsfeedForm] = useState(false);
    const [editingNewsfeedItem, setEditingNewsfeedItem] = useState<NewsfeedItem | null>(null);
    const [newsfeedForm, setNewsfeedForm] = useState({
        title: '',
        description: '',
        mediaUrl: '',
        mediaType: '',
        isActive: true,
    });
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    // ref no longer needed after moving to dynamic file picker

    const openFilePicker = () => {
        // Create input dynamically to avoid any ref/click timing restrictions
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*,video/*';
        input.onchange = (event: Event) => {
            const target = event.target as HTMLInputElement;
            const file = target.files?.[0] || null;
            if (file) {
                const extension = file.name.split('.').pop()?.toLowerCase();
                let mediaType = '';
                if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension || '')) {
                    mediaType = 'image';
                } else if (['mp4', 'webm', 'ogg', 'mov'].includes(extension || '')) {
                    mediaType = 'video';
                }
                setSelectedFile(file);
                setNewsfeedForm(prev => ({ ...prev, mediaType }));
            }
        };
        input.click();
    };

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            switch (activeTab) {
                case 'stats':
                    const statsResponse = await adminApi.getStats();
                    if (statsResponse.status === 'success' && statsResponse.data) {
                        setStats(statsResponse.data);
                    } else {
                        setError(statsResponse.message || 'Failed to load stats');
                    }
                    break;
                case 'newsfeed':
                    const newsfeedResponse = await adminApi.getAllNewsfeedItems();
                    if (newsfeedResponse.status === 'success' && newsfeedResponse.data) {
                        setNewsfeedItems(newsfeedResponse.data);
                    } else {
                        setError(newsfeedResponse.message || 'Failed to load newsfeed items');
                    }
                    break;
                case 'feedback':
                    const feedbackResponse = await adminApi.getAllFeedback();
                    if (feedbackResponse.status === 'success' && feedbackResponse.data) {
                        setFeedbackItems(feedbackResponse.data.feedback);
                    } else {
                        setError(feedbackResponse.message || 'Failed to load feedback');
                    }
                    break;
            }
        } catch (err) {
            setError('Failed to load data');
            console.error('Error loading data:', err);
        } finally {
            setLoading(false);
        }
    }, [activeTab]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleCreateNewsfeedItem = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const response = await adminApi.createNewsfeedItem(newsfeedForm);
            if (response.status === 'success') {
                setShowNewsfeedForm(false);
                setNewsfeedForm({
                    title: '',
                    description: '',
                    mediaUrl: '',
                    mediaType: '',
                    isActive: true,
                });
                setSelectedFile(null);
                loadData(); // Reload newsfeed items
            } else {
                setError(response.message || 'Failed to create newsfeed item');
            }
        } catch (err) {
            setError('Failed to create newsfeed item');
            console.error('Error creating newsfeed item:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateNewsfeedItem = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingNewsfeedItem) return;

        setLoading(true);
        setError(null);

        try {
            const response = await adminApi.updateNewsfeedItem(
                editingNewsfeedItem.id,
                newsfeedForm
            );
            if (response.status === 'success') {
                setEditingNewsfeedItem(null);
                setNewsfeedForm({
                    title: '',
                    description: '',
                    mediaUrl: '',
                    mediaType: '',
                    isActive: true,
                });
                loadData(); // Reload newsfeed items
            } else {
                setError(response.message || 'Failed to update newsfeed item');
            }
        } catch (err) {
            setError('Failed to update newsfeed item');
            console.error('Error updating newsfeed item:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteNewsfeedItem = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this newsfeed item?')) return;

        setLoading(true);
        setError(null);

        try {
            const response = await adminApi.deleteNewsfeedItem(id);
            if (response.status === 'success') {
                loadData(); // Reload newsfeed items
            } else {
                setError(response.message || 'Failed to delete newsfeed item');
            }
        } catch (err) {
            setError('Failed to delete newsfeed item');
            console.error('Error deleting newsfeed item:', err);
        } finally {
            setLoading(false);
        }
    };

    const startEditNewsfeedItem = (item: NewsfeedItem) => {
        setEditingNewsfeedItem(item);
        setNewsfeedForm({
            title: item.title,
            description: item.description || '',
            mediaUrl: item.mediaUrl || '',
            mediaType: item.mediaType || '',
            isActive: item.isActive,
        });
        setShowNewsfeedForm(true);
    };

    const cancelNewsfeedForm = () => {
        setShowNewsfeedForm(false);
        setEditingNewsfeedItem(null);
        setNewsfeedForm({
            title: '',
            description: '',
            mediaUrl: '',
            mediaType: '',
            isActive: true,
        });
        setSelectedFile(null);
    };

    // removed unused handleFileSelect; selection handled in openFilePicker

    // removed unused triggerFileInput; using openFilePicker instead

    const handleFileUpload = async () => {
        if (!selectedFile) {
            console.log('No file selected, cannot upload');
            return;
        }

        setUploading(true);
        setError(null);

        try {
            // If we're creating a new item, we need to create it first
            let newsfeedItemId = editingNewsfeedItem?.id;

            if (!newsfeedItemId) {
                // Create the newsfeed item first
                const createResponse = await adminApi.createNewsfeedItem({
                    title: newsfeedForm.title || 'Untitled',
                    description: newsfeedForm.description,
                    isActive: newsfeedForm.isActive,
                });

                if (createResponse.status === 'success' && createResponse.data) {
                    newsfeedItemId = createResponse.data.id;
                } else {
                    setError(createResponse.message || 'Failed to create newsfeed item');
                    return;
                }
            }

            // Get the upload URL from the API
            const uploadResponse = await adminApi.uploadNewsfeedMedia({
                newsfeedItemId: newsfeedItemId,
                filename: selectedFile.name,
                contentType: selectedFile.type,
            });

            if (uploadResponse.status === 'success' && uploadResponse.data) {
                // Upload the file to S3 using the media URL
                const uploadToS3 = await fetch(uploadResponse.data.signedUrl, {
                    method: 'PUT',
                    body: selectedFile,
                    headers: {
                        'Content-Type': selectedFile.type,
                    },
                });

                if (uploadToS3.ok) {
                    // Update the newsfeed item with the media URL
                    const updateResponse = await adminApi.updateNewsfeedItem(newsfeedItemId, {
                        mediaUrl: uploadResponse.data.mediaUrl,
                        mediaType: uploadResponse.data.mediaType,
                    });

                    if (updateResponse.status === 'success') {
                        setNewsfeedForm({
                            ...newsfeedForm,
                            mediaUrl: uploadResponse.data.mediaUrl,
                            mediaType: uploadResponse.data.mediaType,
                        });
                        setSelectedFile(null);

                        // If we were creating a new item, set it as editing now
                        if (!editingNewsfeedItem && updateResponse.data) {
                            setEditingNewsfeedItem(updateResponse.data);
                        }

                        loadData(); // Reload to get updated data
                    } else {
                        setError(updateResponse.message || 'Failed to update newsfeed item');
                    }
                } else {
                    setError('Failed to upload file to storage');
                }
            } else {
                setError(uploadResponse.message || 'Failed to get upload URL');
            }
        } catch (err) {
            setError('Failed to upload file');
            console.error('File upload error:', err);
        } finally {
            setUploading(false);
        }
    };

    const renderStats = () => (
        <div className='space-y-6'>
            <h2 className='text-2xl font-bold text-gray-900'>System Statistics</h2>
            {stats ? (
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
                    <div className='bg-white p-6 rounded-lg shadow'>
                        <h3 className='text-lg font-medium text-gray-900'>Total Players</h3>
                        <p className='text-3xl font-bold text-blue-600'>{stats.totalPlayers}</p>
                    </div>
                    <div className='bg-white p-6 rounded-lg shadow'>
                        <h3 className='text-lg font-medium text-gray-900'>Online Players</h3>
                        <p className='text-3xl font-bold text-blue-600'>{stats.onlinePlayers}</p>
                    </div>
                    <div className='bg-white p-6 rounded-lg shadow'>
                        <h3 className='text-lg font-medium text-gray-900'>Single Player Games</h3>
                        <p className='text-3xl font-bold text-blue-600'>
                            {stats.singlePlayerGames}
                        </p>
                    </div>
                    <div className='bg-white p-6 rounded-lg shadow'>
                        <h3 className='text-lg font-medium text-gray-900'>Multiplayer Games</h3>
                        <p className='text-3xl font-bold text-blue-600'>{stats.multiplayerGames}</p>
                    </div>
                    <div className='bg-white p-6 rounded-lg shadow'>
                        <h3 className='text-lg font-medium text-gray-900'>
                            Active Paid Subscriptions
                        </h3>
                        <p className='text-3xl font-bold text-blue-600'>
                            {stats.activePaidSubscriptions}
                        </p>
                    </div>
                    <div className='bg-white p-6 rounded-lg shadow'>
                        <h3 className='text-lg font-medium text-gray-900'>Active Free Trials</h3>
                        <p className='text-3xl font-bold text-blue-600'>{stats.activeFreeTrials}</p>
                    </div>
                    <div className='bg-white p-6 rounded-lg shadow'>
                        <h3 className='text-lg font-medium text-gray-900'>Player Trial Invites</h3>
                        <p className='text-3xl font-bold text-blue-600'>
                            {stats.playerTrialInvites}
                        </p>
                    </div>
                </div>
            ) : (
                <div className='text-center py-8'>
                    <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto'></div>
                    <p className='mt-4 text-gray-600'>Loading statistics...</p>
                </div>
            )}
        </div>
    );

    const renderNewsfeed = () => (
        <div className='space-y-6'>
            <div className='flex justify-between items-center'>
                <h2 className='text-2xl font-bold text-gray-900'>Newsfeed Management</h2>
                <button
                    onClick={() => setShowNewsfeedForm(true)}
                    className='bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors'
                >
                    Add Newsfeed Item
                </button>
            </div>

            {showNewsfeedForm && (
                <div className='bg-white p-6 rounded-lg shadow'>
                    <h3 className='text-lg font-medium text-gray-900 mb-4'>
                        {editingNewsfeedItem ? 'Edit Newsfeed Item' : 'Create Newsfeed Item'}
                    </h3>
                    <form
                        onSubmit={
                            editingNewsfeedItem
                                ? handleUpdateNewsfeedItem
                                : handleCreateNewsfeedItem
                        }
                    >
                        <div className='space-y-4'>
                            <div>
                                <label className='block text-sm font-medium text-gray-700'>
                                    Title
                                </label>
                                <input
                                    type='text'
                                    value={newsfeedForm.title}
                                    onChange={e =>
                                        setNewsfeedForm({ ...newsfeedForm, title: e.target.value })
                                    }
                                    className='mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                                    required
                                />
                            </div>
                            <div>
                                <label className='block text-sm font-medium text-gray-700'>
                                    Description
                                </label>
                                <textarea
                                    value={newsfeedForm.description}
                                    onChange={e =>
                                        setNewsfeedForm({
                                            ...newsfeedForm,
                                            description: e.target.value,
                                        })
                                    }
                                    className='mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                                    rows={3}
                                />
                            </div>
                            <div>
                                <label className='block text-sm font-medium text-gray-700 mb-2'>
                                    Upload Media
                                </label>
                                <div className='space-y-3'>
                                    <button
                                        type='button'
                                        onClick={openFilePicker}
                                        className='bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors'
                                    >
                                        Choose File
                                    </button>
                                    {selectedFile && (
                                        <div className='flex items-center space-x-3'>
                                            <span className='text-sm text-gray-600'>
                                                Selected: {selectedFile.name}
                                            </span>
                                            <button
                                                type='button'
                                                onClick={handleFileUpload}
                                                disabled={uploading}
                                                className='bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed'
                                            >
                                                {uploading ? 'Uploading...' : 'Upload'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {newsfeedForm.mediaUrl && (
                                <div>
                                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                                        Media Preview
                                    </label>
                                    <div className='border border-gray-300 rounded-md p-4'>
                                        {newsfeedForm.mediaType === 'image' && (
                                            <img
                                                src={newsfeedForm.mediaUrl}
                                                alt='Media preview'
                                                className='max-w-full h-auto max-h-64 object-contain'
                                                onError={e => {
                                                    e.currentTarget.style.display = 'none';
                                                }}
                                            />
                                        )}
                                        {newsfeedForm.mediaType === 'video' && (
                                            <video
                                                src={newsfeedForm.mediaUrl}
                                                controls
                                                className='max-w-full h-auto max-h-64'
                                                onError={e => {
                                                    e.currentTarget.style.display = 'none';
                                                }}
                                            >
                                                Your browser does not support the video tag.
                                            </video>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className='flex items-center'>
                                <input
                                    type='checkbox'
                                    id='isActive'
                                    checked={newsfeedForm.isActive}
                                    onChange={e =>
                                        setNewsfeedForm({
                                            ...newsfeedForm,
                                            isActive: e.target.checked,
                                        })
                                    }
                                    className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
                                />
                                <label
                                    htmlFor='isActive'
                                    className='ml-2 block text-sm text-gray-900'
                                >
                                    Active
                                </label>
                            </div>
                        </div>
                        <div className='mt-6 flex space-x-3'>
                            <button
                                type='submit'
                                className='bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors'
                            >
                                {editingNewsfeedItem ? 'Update' : 'Create'}
                            </button>
                            <button
                                type='button'
                                onClick={cancelNewsfeedForm}
                                className='bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors'
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className='bg-white rounded-lg shadow overflow-hidden'>
                <table className='min-w-full divide-y divide-gray-200'>
                    <thead className='bg-gray-50'>
                        <tr>
                            <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                                Title
                            </th>
                            <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                                Status
                            </th>
                            <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                                Media
                            </th>
                            <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                                Created
                            </th>
                            <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className='bg-white divide-y divide-gray-200'>
                        {newsfeedItems.map(item => (
                            <tr key={item.id}>
                                <td className='px-6 py-4'>
                                    <div className='text-sm font-medium text-gray-900'>
                                        {item.title}
                                    </div>
                                    {item.description && (
                                        <div className='text-sm text-gray-500 max-w-xs break-words'>
                                            {item.description}
                                        </div>
                                    )}
                                </td>
                                <td className='px-6 py-4 whitespace-nowrap'>
                                    <span
                                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                            item.isActive
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-red-100 text-red-800'
                                        }`}
                                    >
                                        {item.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td className='px-6 py-4 whitespace-nowrap'>
                                    <input
                                        type='checkbox'
                                        checked={!!item.mediaUrl}
                                        readOnly
                                        className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
                                    />
                                </td>
                                <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                                    {new Date(item.created).toLocaleDateString()}
                                </td>
                                <td className='px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2'>
                                    <button
                                        onClick={() => startEditNewsfeedItem(item)}
                                        className='text-blue-600 hover:text-blue-900'
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => handleDeleteNewsfeedItem(item.id)}
                                        className='text-red-600 hover:text-red-900'
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    const renderFeedback = () => (
        <div className='space-y-6'>
            <h2 className='text-2xl font-bold text-gray-900'>User Feedback</h2>
            <div className='bg-white rounded-lg shadow overflow-hidden'>
                <table className='min-w-full divide-y divide-gray-200'>
                    <thead className='bg-gray-50'>
                        <tr>
                            <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                                Player ID
                            </th>
                            <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                                Feedback
                            </th>
                            <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                                Date
                            </th>
                        </tr>
                    </thead>
                    <tbody className='bg-white divide-y divide-gray-200'>
                        {feedbackItems.map(item => (
                            <tr key={item.id}>
                                <td className='px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900'>
                                    {item.playerId}
                                </td>
                                <td className='px-6 py-4 text-sm text-gray-900'>{item.text}</td>
                                <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                                    {new Date(item.created).toLocaleDateString()}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <div className='min-h-screen bg-gray-50'>
            <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
                <div className='mb-8'>
                    <h1 className='text-3xl font-bold text-gray-900'>Admin Dashboard</h1>
                </div>

                {error && (
                    <div className='mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded'>
                        {error}
                    </div>
                )}

                <div className='mb-6'>
                    <nav className='flex space-x-8'>
                        <button
                            onClick={() => handleTabChange('stats')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'stats'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            Statistics
                        </button>
                        <button
                            onClick={() => handleTabChange('newsfeed')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'newsfeed'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            Newsfeed
                        </button>
                        <button
                            onClick={() => handleTabChange('feedback')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'feedback'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            Feedback
                        </button>
                    </nav>
                </div>

                {loading && (
                    <div className='text-center py-8'>
                        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto'></div>
                        <p className='mt-4 text-gray-600'>Loading...</p>
                    </div>
                )}

                {!loading && (
                    <>
                        {activeTab === 'stats' && renderStats()}
                        {activeTab === 'newsfeed' && renderNewsfeed()}
                        {activeTab === 'feedback' && renderFeedback()}
                    </>
                )}
            </div>
        </div>
    );
};

export default AdminDashboard;
